/**
 * Product Gallery Zoom Component
 * Creates a fullscreen zoom viewer when product images are clicked
 * Based on jessicamccormack.com reference design
 */
class ProductGallery extends HTMLElement {
  constructor() {
    super();

    this.mobileMatchMedia = window.matchMedia('(max-width: 1024px)');
    this.galleryViewerElement = this.querySelector('.product-gallery-viewer');
    this.galleryViewerElementImage = this.galleryViewerElement.querySelector('.product-gallery-viewer-image');
    this.galleryViewerImage = this.galleryViewerElement.querySelector('[data-viewer-image]');
    this.galleryClose = this.querySelector('[data-close]');

    this.productGalleryThumbnailsHeight = ((32 + 53 + 32) / 2) * -1;

    this.thumbnails = this.querySelectorAll('.product-gallery-thumbnails button');
    this.thumbnailMain = this.querySelector('.product-gallery-thumbnails');
    this.activeThumbnail = this.thumbnails[0];

    this.isDragging = false;
    this.imageViewerOpen = false;
    this.isViewing360 = false;
    this.galleryActiveImage = null;
    this.currentScroll = 0;

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Thumbnail click handlers
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', this.handleThumbnailClick.bind(this));
    });

    // Close button handler
    if (this.galleryClose) {
      this.galleryClose.addEventListener('click', this.closeGallery.bind(this));
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.imageViewerOpen) {
        this.closeGallery();
      }
    });

    // Mobile drag handlers
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('touchmove', this.onTouchMove.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));
    document.addEventListener('touchend', this.onMouseUp.bind(this));

    if (this.galleryViewerImage) {
      this.galleryViewerImage.addEventListener('mousedown', this.onMouseDown.bind(this));
      this.galleryViewerImage.addEventListener('touchstart', this.onTouchStart.bind(this));
      this.galleryViewerImage.addEventListener('click', this.onClick.bind(this));
    }

    // Window resize handler
    window.addEventListener('resize', this.debounce(this.onResize.bind(this), 150));

    // Listen for product media clicks from outside
    this.setupProductMediaClickListeners();
  }

  setupProductMediaClickListeners() {
    // Find all product media images on the page and add click listeners
    const productMediaImages = document.querySelectorAll('.product__media img, .product-media-container img');

    productMediaImages.forEach((img) => {
      img.style.cursor = 'pointer';
      img.addEventListener('click', (e) => {
        e.preventDefault();
        const mediaId = img.dataset.mediaId || img.closest('[data-media-id]')?.dataset.mediaId?.split('-').pop();
        if (mediaId) {
          this.openGalleryWithMediaId(mediaId, img);
        }
      });

      // Keyboard accessibility
      img.setAttribute('tabindex', '0');
      img.setAttribute('role', 'button');
      img.setAttribute('aria-label', 'Open image zoom viewer');
      img.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const mediaId = img.dataset.mediaId || img.closest('[data-media-id]')?.dataset.mediaId?.split('-').pop();
          if (mediaId) {
            this.openGalleryWithMediaId(mediaId, img);
          }
        }
      });
    });
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  onResize() {
    if (this.imageViewerOpen) {
      requestAnimationFrame(() => {
        this.setInitialPosition();
      });
    }
  }

  onMouseMove(event) {
    if (!this.mobileMatchMedia.matches || this.isViewing360) {
      return;
    }
    if (this.isDragging && this.galleryActiveImage) {
      const dx = event.clientX - this.startX;
      const dy = event.clientY - this.startY;
      const newLeft = this.initialX + dx;
      const newTop = this.initialY + dy;

      if (newLeft < 1 && newLeft + this.galleryActiveImage.offsetWidth > window.innerWidth) {
        this.galleryViewerImage.style.left = this.initialX + dx + 'px';
      }

      if (newTop < 1 && newTop + this.galleryActiveImage.offsetHeight > window.innerHeight) {
        this.galleryViewerImage.style.top = this.initialY + dy + 'px';
      }
    }
  }

  onTouchMove(event) {
    if (this.isDragging && this.galleryActiveImage) {
      const touches = event.changedTouches;
      for (let i = 0; i < touches.length; i++) {
        const dx = touches[i].clientX - this.startX;
        const dy = touches[i].clientY - this.startY;
        const newLeft = this.initialX + dx;
        const newTop = this.initialY + dy;

        if (newLeft < 1 && newLeft + this.galleryActiveImage.offsetWidth > window.innerWidth) {
          this.galleryViewerImage.style.left = this.initialX + dx + 'px';
        }
        if (newTop < 1 && newTop + this.galleryActiveImage.offsetHeight > window.innerHeight) {
          this.galleryViewerImage.style.top = this.initialY + dy + 'px';
        }
      }
    }
  }

  onTouchStart(event) {
    this.isDragging = true;
    this.startX = event.changedTouches[0].clientX;
    this.startY = event.changedTouches[0].clientY;
    this.initialX = this.galleryViewerImage.offsetLeft;
    this.initialY = this.galleryViewerImage.offsetTop;
    this.galleryViewerImage.style.cursor = 'grabbing';
  }

  onMouseUp() {
    if (this.isDragging) {
      this.isDragging = false;
      if (this.galleryViewerImage) {
        this.galleryViewerImage.style.cursor = 'grab';
      }
    }
  }

  onMouseDown(event) {
    if (!this.mobileMatchMedia.matches || this.isViewing360) {
      return;
    }
    this.isDragging = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.initialX = this.galleryViewerImage.offsetLeft;
    this.initialY = this.galleryViewerImage.offsetTop;
    this.galleryViewerImage.style.cursor = 'grabbing';
  }

  onClick(event) {
    if (!this.mobileMatchMedia.matches) {
      this.closeGallery();
    }
  }

  setInitialPosition() {
    if (!this.imageViewerOpen || !this.galleryActiveImage) {
      return;
    }

    if (!this.mobileMatchMedia.matches) {
      // Desktop: center vertically via scroll
      const heightDifference = this.galleryViewerImage.scrollHeight - window.innerHeight;
      this.galleryViewerImage.scrollTop = heightDifference / 2;
      this.galleryViewerImage.style.left = '0px';
    } else {
      // Mobile: center the image
      const topPosition =
        (this.galleryActiveImage.offsetHeight - window.innerHeight) / 2 - this.productGalleryThumbnailsHeight;
      const leftPosition = (this.galleryActiveImage.offsetWidth - window.innerWidth) / 2;
      this.galleryViewerImage.style.top = `${-topPosition}px`;
      this.galleryViewerImage.style.left = `${-leftPosition}px`;
    }
  }

  handleThumbnailClick(event) {
    const thisTarget = event.currentTarget;
    const mediaId = thisTarget.dataset.mediaId;

    // Hide current active image
    if (this.galleryActiveImage) {
      this.galleryActiveImage.classList.add('is-hidden');
    }

    // Find and show new active image
    this.galleryActiveImage = this.galleryViewerImage.querySelector(`[data-media-id="${mediaId}"]`);

    if (this.galleryActiveImage) {
      this.galleryActiveImage.classList.remove('is-hidden');
      this.setActiveThumbnail(mediaId);

      // Reset scroll position
      const heightDifference = this.galleryViewerImage.scrollHeight - window.innerHeight;
      this.galleryViewerImage.scrollTop = heightDifference / 2;

      this.setInitialPosition();
    }
  }

  openGalleryWithMediaId(mediaId, triggerElement) {
    this.galleryTriggerImage = triggerElement;

    // Add overflow hidden during animation
    this.galleryViewerImage.classList.add('overflow-hidden');

    // Find the active image in the zoom viewer
    this.galleryActiveImage = this.galleryViewerImage.querySelector(`[data-media-id="${mediaId}"]`);

    // If exact media ID not found, try to find first image
    if (!this.galleryActiveImage) {
      this.galleryActiveImage = this.galleryViewerImage.querySelector('img');
    }

    if (!this.galleryActiveImage) {
      console.warn('No gallery image found');
      return;
    }

    // Set all images to eager loading
    this.galleryViewerImage.querySelectorAll('img').forEach((image) => {
      image.setAttribute('loading', 'eager');
    });

    // Show the active image, hide others
    this.galleryViewerImage.querySelectorAll('img').forEach((img) => {
      if (img.dataset.mediaId === mediaId) {
        img.classList.remove('is-hidden');
      } else {
        img.classList.add('is-hidden');
      }
    });

    this.imageViewerOpen = true;

    // Show the gallery viewer (remove is-hidden)
    this.galleryViewerElement.classList.remove('is-hidden');

    // Trigger slide-up animation
    setTimeout(() => {
      this.setInitialPosition();

      const heightDifference = this.galleryViewerImage.scrollHeight - window.innerHeight;
      this.galleryViewerImage.scrollTop = heightDifference / 2;

      this.disableBodyScroll();

      setTimeout(() => {
        // Animate slide up (remove is-slide-down, add is-visible)
        this.galleryViewerElement.classList.remove('is-slide-down');
        this.galleryViewerElement.classList.add('is-visible');

        // Set active thumbnail
        this.setActiveThumbnail(mediaId);

        // Wait for transition to complete
        this.waitForTransition(this.galleryViewerElement).then(() => {
          const heightDifference = this.galleryViewerImage.scrollHeight - window.innerHeight;
          this.galleryViewerImage.scrollTop = heightDifference / 2;
          this.galleryViewerImage.classList.remove('overflow-hidden');

          // Show thumbnails
          this.thumbnailMain.classList.remove('is-hidden');

          setTimeout(() => {
            this.thumbnailMain.classList.remove('is-transparent');
            this.galleryClose.classList.remove('is-transparent');
          }, 100);
        });
      }, 50);
    }, 50);
  }

  setActiveThumbnail(mediaId) {
    const newActiveThumbnail = this.querySelector(`.thumbnail-button[data-media-id="${mediaId}"]`);

    if (this.activeThumbnail) {
      this.activeThumbnail.classList.remove('is-active');
      this.activeThumbnail.setAttribute('aria-current', 'false');
    }

    if (newActiveThumbnail) {
      this.activeThumbnail = newActiveThumbnail;
      this.activeThumbnail.classList.add('is-active');
      this.activeThumbnail.setAttribute('aria-current', 'true');
    }
  }

  closeGallery() {
    // Fade out thumbnails and close button
    this.thumbnailMain.classList.add('is-transparent');
    this.galleryClose.classList.add('is-transparent');
    this.imageViewerOpen = false;
    this.isViewing360 = false;

    this.waitForTransition(this.thumbnailMain).then(() => {
      this.thumbnailMain.classList.add('is-hidden');
      this.galleryViewerImage.classList.add('overflow-hidden');

      this.enableBodyScroll();

      setTimeout(() => {
        // Animate slide down
        this.galleryViewerElement.classList.remove('is-visible');
        this.galleryViewerElement.classList.add('is-slide-down');

        this.waitForTransition(this.galleryViewerElement).then(() => {
          this.galleryViewerElement.classList.add('is-hidden');

          // Hide all images
          this.galleryViewerImage.querySelectorAll('img:not(.is-hidden)').forEach((image) => {
            image.classList.add('is-hidden');
          });

          // Return focus to trigger element
          setTimeout(() => {
            if (this.galleryTriggerImage) {
              this.galleryTriggerImage.focus();
            }
          }, 100);
        });
      }, 66);
    });
  }

  waitForTransition(element) {
    return new Promise((resolve) => {
      const handleTransitionEnd = (e) => {
        if (e.target === element) {
          element.removeEventListener('transitionend', handleTransitionEnd);
          resolve();
        }
      };

      element.addEventListener('transitionend', handleTransitionEnd);

      // Fallback in case transition doesn't fire
      setTimeout(resolve, 600);
    });
  }

  disableBodyScroll() {
    this.currentScroll = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.currentScroll}px`;
    document.body.style.width = '100%';
  }

  enableBodyScroll() {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this.currentScroll);
  }
}

customElements.define('product-gallery', ProductGallery);
